import { FC, useEffect, useRef } from 'react';

import { IVersionMetadataErrors, isNameTaken } from '@entities/routing/config-version';

interface IProps {
  idPrefix: string;
  name: string;
  comment: string;
  onNameChange: (name: string) => void;
  onCommentChange: (comment: string) => void;
  // Other versions' names, for the duplicate notice.
  takenNames: string[];
  // Rendered under the input each belongs to.
  errors: IVersionMetadataErrors;
  disabled: boolean;
  nameLabel?: string;
  commentPlaceholder?: string;
  // Both forms stop on a duplicate; the wording fits each (save vs rename).
  duplicateNameHint?: string;
}

// Name + comment inputs shared by Save Draft and Edit details. No maxLength: an
// over-long paste is refused, not cut.
const VersionMetadataFields: FC<IProps> = ({
  idPrefix,
  name,
  comment,
  onNameChange,
  onCommentChange,
  takenNames,
  errors,
  disabled,
  nameLabel = 'Name',
  commentPlaceholder = 'What is this version for?',
  duplicateNameHint = 'Version names must be unique — choose a different name.',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // The form owns the block (disabled button + submit guard); this only shows the notice.
  const isDuplicateName = isNameTaken(name, takenNames);
  const nameErrorId = `${idPrefix}-name-error`;
  const duplicateNoticeId = `${idPrefix}-name-duplicate`;
  // Whatever sits under the name input is announced with it, so a disabled Save has a reason.
  const nameDescribedBy =
    [errors.name && nameErrorId, isDuplicateName && duplicateNoticeId].filter(Boolean).join(' ') ||
    undefined;

  return (
    <>
      <label className='routing-version-form__label' htmlFor={`${idPrefix}-name`}>
        {nameLabel}
      </label>
      <input
        id={`${idPrefix}-name`}
        ref={inputRef}
        type='text'
        value={name}
        onChange={event => onNameChange(event.target.value)}
        aria-invalid={Boolean(errors.name) || isDuplicateName}
        aria-describedby={nameDescribedBy}
        disabled={disabled}
      />
      {errors.name && (
        <p id={nameErrorId} className='routing-version-form__error' role='alert'>
          {errors.name}
        </p>
      )}
      {isDuplicateName && (
        <p id={duplicateNoticeId} className='routing-version-form__warning'>
          Another version is already named “{name.trim()}”. {duplicateNameHint}
        </p>
      )}

      <label className='routing-version-form__label' htmlFor={`${idPrefix}-comment`}>
        Comment <span className='routing-version-form__optional'>(optional)</span>
      </label>
      <textarea
        id={`${idPrefix}-comment`}
        rows={3}
        value={comment}
        placeholder={commentPlaceholder}
        onChange={event => onCommentChange(event.target.value)}
        aria-invalid={Boolean(errors.comment)}
        disabled={disabled}
      />
      {errors.comment && (
        <p className='routing-version-form__error' role='alert'>
          {errors.comment}
        </p>
      )}
    </>
  );
};

export default VersionMetadataFields;
